














































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { User, ProjectStreams, Props, Global, Projects } from '@/store';
import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';
import OverviewInfo from './components/OverviewInfo.vue';
import CabinetRow from './components/CabinetRow.vue';
import VirtualList from 'vue-virtual-scroll-list';
import MQTT from '@/modules/MQTT';
import { isEqual } from 'lodash';
import {
  fetchCabinetsSlim,
  fetchCabinetsDetails,
  fetchAllFixtureCounts,
  FixtureCount,
  getCabinetSnapshot,
  isCabinetSnapshotFresh,
  primeCabinetSnapshot,
  addCabinetDetailsToSnapshot
} from './services/CabinetManagementApi';

@Component({
  components: {
    OverviewInfo,
    CabinetRow,
    VirtualList
  }
})
class CabinetManagement extends Vue {
  @Global.State('lang') lang;
  @Global.State('timezone') projectTimezone;
  @Global.State('dusk_dawn_data') dusk_dawn_data;
  @Global.State('mqtt_version_by_class') mqtt_version_by_class;
  @Global.State('selected_managed_area') selected_managed_area;
  @Global.Mutation('setSelectedManagedArea') setSelectedManagedArea;
  @Global.Mutation('setPageTitle') setPageTitle;
  @Props.State('cabinet_meta_device_options') cabinet_meta_device_options;
  @User.State('project') project;
  @User.State('username') username;
  @ProjectStreams.State('next_activation_by_cabinet') next_activation_by_cabinet;
  @ProjectStreams.State('relativeStreamHistory') relativeStreamHistory;
  @ProjectStreams.State('digital_input_by_cabinet') digital_input_by_cabinet;
  @Projects.State('projectAlerts') projectAlerts;

  fixture_for_overview = 0;
  cabinet_row_component = CabinetRow;
  mqtt = MQTT.instance;
  isLoading = true;
  cabinetsList = []; // slim cabinets list (minimal columns)
  cabinets_by_id = new Map(); // id -> cabinet (same objects as cabinetsList)
  hydrated_ids: Set<string> = new Set(); // ids already hydrated with full cabinet details
  hydrating_ids: Set<string> = new Set(); // ids whose detail-hydration is in-flight (race guard)
  fixtures_by_cabinet = {}; // id -> { total, connected_active, percentage, loaded }
  fixture_counts_by_human_id: Record<string, FixtureCount> = {};
  fixture_counts_loaded = false;
  _rebuild_scheduled = false;
  meta_device_fields = [];
  managed_area_map = new Map();
  filtered_managed_area_map = new Map();
  selectedValues = [];
  search_by = {};
  search = '';
  filteredCabinets = [];
  focused = true;
  panel_is_open = {};
  flex_settings = { 
    'Cabinet ID': {he: 'md3', en: 'md3'}, 
    'Controlled Fixtures': {he: 'md2', en: 'md2'},
    'Services': {he: 'md2', en: 'md2'},
    'Operation': {he: 'md2', en: 'md2'},
    'Monitor': {he: 'md2', en: 'md2'},
    'Operation Window': {he: 'md2', en: 'md2'}
  };
  disabled_color = 'rgba(0,0,0,0.12)';
  devices_status = {}; // id -> bool (true once the cabinet has been hydrated with full details)
  inputs_status = {};
  outputs_status = {};
  listener_ids_versions = new Map(); // device id -> mqtt version
  utils_module = Utils;

  created() {
    this.setPageTitle('Cabinets');
    this.mqtt.init();
    // 15-min snapshot cache — skip network entirely on quick revisits.
    // Past TTL or first visit, fall back to the regular fetch flow which
    // surfaces the loading spinner via `isLoading`.
    if (this.tryRestoreFromCache()) return;
    this.fetchSlimCabinetsList();
    this.fetchFixtureCounts();
  }

  tryRestoreFromCache(): boolean {
    if (!this.project || !this.project.id || !this.project.company) return false;
    if (!isCabinetSnapshotFresh(this.project.company, this.project.id)) return false;
    const snapshot = getCabinetSnapshot(this.project.company, this.project.id);
    if (!snapshot || !snapshot.cabinets.length) return false;

    this.fixture_counts_by_human_id = snapshot.fixtureCounts || {};
    this.fixture_counts_loaded = true;
    // Reuse the slim-batch path so per-cabinet maps / status objects get
    // (re)initialized exactly the same way as a fresh stream would.
    this.onSlimBatch(snapshot.cabinets);
    this.isLoading = false;
    this.rebuildManagedAreaMap();
    this._rebuild_scheduled = false;
    this.$nextTick(() => this.setDefaultPanelOpen());
    return true;
  }

  mounted(){
    this.$nextTick(() => {
      const input = this.$refs.search_combobox && (this.$refs.search_combobox as any)['$el'].querySelector('input');
      if (input) {
        input.addEventListener('keydown', this.onKeydown);
      }
    });
  }

  async fetchSlimCabinetsList(){
    if (!this.project || !this.project.id || !this.project.company) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    try {
      await fetchCabinetsSlim({
        companyId: this.project.company,
        projectId: this.project.id,
        defaultLocation: undefined,
        onBatch: (batch) => this.onSlimBatch(batch)
      });
    } catch (e) {
      console.error('[CabinetManagement] slim cabinets fetch failed', e);
    } finally {
      this.isLoading = false;
      this.rebuildManagedAreaMap();
      this._rebuild_scheduled = false;
      if (this.cabinetsList.length && this.project && this.project.id && this.project.company) {
        primeCabinetSnapshot(this.project.company, this.project.id, {
          cabinets: this.cabinetsList,
          fixtureCounts: this.fixture_counts_by_human_id || {}
        });
      }
      this.$nextTick(() => this.setDefaultPanelOpen());
    }
  }

  onSlimBatch(batch){
    const accepted = [];
    batch.forEach((cabinet) => {
      if (!cabinet || !cabinet.id) return;
      if (this.cabinets_by_id.has(cabinet.id)) return;
      this.enrichSlim(cabinet);
      this.cabinets_by_id.set(cabinet.id, cabinet);
      Vue.set(this.devices_status, cabinet.id, false);
      Vue.set(this.inputs_status, cabinet.id, {});
      Vue.set(this.outputs_status, cabinet.id, {});
      if (this.fixture_counts_loaded) {
        this.applyFixtureCount(cabinet);
      } else {
        Vue.set(this.fixtures_by_cabinet, cabinet.id, {
          total: 0, connected_active: 0, percentage: 0, loaded: false
        });
      }
      accepted.push(cabinet);
    });
    if (accepted.length) {
      this.cabinetsList.push(...accepted);
      this.scheduleManagedAreaRebuild();
    }
  }

  scheduleManagedAreaRebuild(){
    if (this._rebuild_scheduled) return;
    this._rebuild_scheduled = true;
    requestAnimationFrame(() => {
      this._rebuild_scheduled = false;
      this.rebuildManagedAreaMap();
    });
  }

  async fetchFixtureCounts(){
    if (!this.project || !this.project.id || !this.project.company) return;
    try {
      this.fixture_counts_by_human_id = await fetchAllFixtureCounts({
        companyId: this.project.company,
        projectId: this.project.id
      });
      this.fixture_counts_loaded = true;
      this.cabinetsList.forEach((cabinet) => this.applyFixtureCount(cabinet));
      // Re-prime so the snapshot reflects the now-loaded fixture counts. If
      // the slim cabinets fetch hasn't finished yet, its `finally` block will
      // prime later.
      if (this.cabinetsList.length && this.project && this.project.id && this.project.company) {
        primeCabinetSnapshot(this.project.company, this.project.id, {
          cabinets: this.cabinetsList,
          fixtureCounts: this.fixture_counts_by_human_id
        });
      }
    } catch (e) {
      console.error('[CabinetManagement] fixture counts fetch failed', e);
    }
  }

  applyFixtureCount(cabinet){
    if (!cabinet.cabinet_id || cabinet.cabinet_id === 'N/A') {
      Vue.set(this.fixtures_by_cabinet, cabinet.id, { total: 0, connected_active: 0, percentage: 0, loaded: true });
      return;
    }
    const counts = this.fixture_counts_by_human_id[cabinet.cabinet_id] || { total: 0, connected_active: 0 };
    const percentage = counts.total ? Math.ceil((counts.connected_active / counts.total) * 100) : 0;
    Vue.set(this.fixtures_by_cabinet, cabinet.id, {
      total: counts.total,
      connected_active: counts.connected_active,
      percentage,
      loaded: true
    });
  }

  /**
   * Slim-data enrichment — derives only fields that depend on slim columns
   * (meta.device + meta.category) so closed panels render correctly.
   */
  enrichSlim(cabinet){
    const meta_device = cabinet['meta.device'] || {};
    cabinet.meta_device = meta_device;
    cabinet.cabinet_id = meta_device.cabinet_id || 'N/A';
    cabinet.description = meta_device.description || '';
    cabinet.cabinet_type = Utils.hasVirtualCabinetClass(cabinet['meta.category']) ? 'virtual' : 'regular';
    cabinet.managed_area = meta_device.managed_area || meta_device.road || '';
    cabinet.linked_managed_area = cabinet.managed_area ? this.getLinkedManagedArea(cabinet.managed_area) : '';
    cabinet.linked_inputs = cabinet.linked_inputs || [];
    cabinet.linked_outputs = cabinet.linked_outputs || [];
  }

  rebuildManagedAreaMap(){
    const area_map = new Map();
    this.cabinetsList.forEach((cabinet) => {
      const key = cabinet.linked_managed_area || 'Other';
      if (!area_map.has(key)) area_map.set(key, []);
      area_map.get(key).push(cabinet);
    });
    const has_other = area_map.has('Other') && area_map.get('Other').length > 0;
    if (has_other) {
      const other = area_map.get('Other');
      area_map.delete('Other');
      this.managed_area_map = new Map([...area_map.entries()].sort((m1, m2) => m1[0].localeCompare(m2[0], undefined, {numeric: true, sensitivity: 'base'})));
      this.managed_area_map.set('Other', other);
    } else {
      this.managed_area_map = new Map([...area_map.entries()].sort((m1, m2) => m1[0].localeCompare(m2[0], undefined, {numeric: true, sensitivity: 'base'})));
    }
    [...this.managed_area_map.values()].forEach((cabinets) => cabinets.sort((c1, c2) => c1.cabinet_id.localeCompare(c2.cabinet_id, undefined, {numeric: true, sensitivity: 'base'})));
    if (!this.search && !this.selectedValues.length) {
      this.filtered_managed_area_map = this.managed_area_map;
    }
  }

  get headers(){
    return ['Cabinet ID', 'Controlled Fixtures', 'Services', 'Operation', 'Monitor', 'Operation Window'];
  }

  setDefaultPanelOpen(){
    if (this.managed_area_map.size === 1) {
      const managed_area = [...this.managed_area_map.keys()][0];
      Vue.set(this.panel_is_open, managed_area, true);
      this.hydratePanel(managed_area);
    }else {
      [...this.managed_area_map.keys()].forEach((managed_area) => Vue.set(this.panel_is_open, managed_area, false));
    }
    if (this.selected_managed_area && !this.panel_is_open[this.selected_managed_area]) {
      this.setPanelIsOpen(this.selected_managed_area);
      const managed_area_element = document.getElementById(`${this.selected_managed_area}-panel`);
      if (managed_area_element) {
        managed_area_element.scrollIntoView();
      }
    }
  }

  setPanelIsOpen(managed_area){
    const next = !this.panel_is_open[managed_area];
    Vue.set(this.panel_is_open, managed_area, next);
    if (next) this.hydratePanel(managed_area);
  }

  /**
   * Hydrates the cabinets in a panel with their full details.
   * Idempotent — already-hydrated cabinets are skipped.
   * Fixture counts are loaded eagerly project-wide (see fetchFixtureCounts).
   */
  async hydratePanel(managed_area){
    const cabinets = (this.filtered_managed_area_map.get(managed_area)
      || this.managed_area_map.get(managed_area)
      || []) as any[];
    if (!cabinets.length) return;

    const cabinets_to_hydrate = cabinets.filter((c) =>
      !this.hydrated_ids.has(c.id) && !this.hydrating_ids.has(c.id)
    );
    cabinets_to_hydrate.forEach((c) => this.hydrating_ids.add(c.id));

    await this.hydrateCabinetsWithDetails(cabinets_to_hydrate);
  }

  async hydrateCabinetsWithDetails(targets){
    if (!targets.length) return;
    try {
      // Reuse cached cabinet details when available; only fetch the missing ids.
      const snapshot = (this.project && this.project.id && this.project.company)
        ? getCabinetSnapshot(this.project.company, this.project.id)
        : null;
      const cachedDetails: any[] = [];
      const toFetch: any[] = [];
      if (snapshot && snapshot.details && snapshot.details.size) {
        targets.forEach((t) => {
          const cached = snapshot.details.get(t.id);
          if (cached) cachedDetails.push(cached);
          else toFetch.push(t);
        });
      } else {
        toFetch.push(...targets);
      }

      let fetched: any[] = [];
      if (toFetch.length) {
        fetched = await fetchCabinetsDetails({
          companyId: this.project.company,
          projectId: this.project.id,
          ids: toFetch.map((c) => c.id)
        });
        if (fetched.length && this.project && this.project.id && this.project.company) {
          addCabinetDetailsToSnapshot(this.project.company, this.project.id, fetched);
        }
      }

      const detailById = new Map<string, any>();
      cachedDetails.forEach((d: any) => detailById.set(d.id, d));
      fetched.forEach((d: any) => detailById.set(d.id, d));
      const chunk_size = 8;
      for (let i = 0; i < targets.length; i += chunk_size) {
        const slice = targets.slice(i, i + chunk_size);
        slice.forEach((cabinet) => {
          const detail = detailById.get(cabinet.id);
          if (detail) this.enrichCabinet(cabinet, detail);
          this.hydrated_ids.add(cabinet.id);
          this.hydrating_ids.delete(cabinet.id);
        });
        if (i + chunk_size < targets.length) {
          await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
        }
      }
    } catch (e) {
      console.error('[CabinetManagement] cabinet details fetch failed', e);
      targets.forEach((c) => this.hydrating_ids.delete(c.id));
    }
  }

  @Watch('cabinet_meta_device_options', { immediate: true })
  setCabinetDataOptions(){
    this.meta_device_fields = Object.keys(this.cabinet_meta_device_options || {});
  }

  replaceSpecialChars(field_name){
    return field_name.includes('_') 
      ? /^[a-zA-Z]+/.test(this.$t(field_name)) 
      ? this.$t(field_name) 
      : field_name.replaceAll('_', ' ')
      : field_name;
  }

  onKeydown(event) {
    if (event.key === 'Backspace') {
      const original_search_by = { ...this.search_by };
      if (this.selectedValues.length && this.selectedValues.length % 2 === 0) {
        const selected_fields = this.selectedValues
          .filter((data) => this.meta_device_fields.includes(data.value))
          .map((data) => data.value);

        const not_selected = [];

        Object.keys(this.search_by).forEach((field) => {
          if (!selected_fields.includes(field)) {
            not_selected.push(field);
          }
        });

        not_selected.forEach((not_selected_field) => delete this.search_by[not_selected_field]);
      }else if (!this.selectedValues.length){
        this.search_by = {};
      }
      if (!isEqual(original_search_by, this.search_by)) {
        this.selectedChanged();
      }
    }
  }

  get items(){
    if ((this.selectedValues.length === 1 && !this.meta_device_fields.includes(this.selectedValues[0].value))
      || (this.selectedValues.length > 1 && !this.meta_device_fields.includes(this.selectedValues[0].value))
      || (this.selectedValues.length % 2 !== 0 && !this.meta_device_fields.includes(this.search) && !this.meta_device_fields.includes(this.selectedValues[this.selectedValues.length - 1]?.value))) 
      return this.selectedValues;
    
    const evenLength = this.selectedValues.length % 2 === 0;
    const field = evenLength ? '' : this.selectedValues.slice(-1)[0].value;
    const values = evenLength
      ? this.meta_device_fields
      : this.cabinet_meta_device_options[this.selectedValues.slice(-1)[0].value] || [];
    const final = evenLength 
      ? values.map((value, index) => ({text: this.$t(PropUtils.getFieldLowerToUpper(value)), value, id: index}))
      : values.map((value, index) => ({text: this.$t(value), value, id: `${field}-${index}`}));

    return final.sort((data1, data2) => data1.text.toString().localeCompare(data2.text.toString(), undefined, {numeric: true, sensitivity: 'base'}));
  }

  @Watch('search')
  updateFreeSearch(){
    if (this.search && !this.selectedValues.length && !this.meta_device_fields.some((field) => field.includes(this.search))){
      this.filterByFreeSearch();
    }else if (!this.selectedValues.length && !this.selected_managed_area){
      if (!isEqual(this.filtered_managed_area_map, this.managed_area_map)) {
        this.filtered_managed_area_map = this.managed_area_map;
      }
      const panel_open = this.managed_area_map.size === 1 ? true : false;
      [...this.managed_area_map.keys()].forEach((managed_area) => Vue.set(this.panel_is_open, managed_area, panel_open));
    }else if (this.selected_managed_area){
      this.setSelectedManagedArea('');
    }
  }

  selectedChanged(){
    const last_index = this.selectedValues.length - 1;
    if (last_index >= 0) {
      const last_value = this.selectedValues[last_index];
      if (this.search && (this.selectedValues.length === 1 
        || this.meta_device_fields.includes(this.selectedValues[0].value) 
        || (this.selectedValues.length % 2 !== 0 && !this.meta_device_fields.includes(this.search)))){
        this.selectedValues[last_index] = typeof last_value === 'string' 
          ? {text: last_value, value: last_value, id: `search-${last_index}`}
          : last_value;
      }
    }

    this.search = '';

    if (this.selectedValues.length && !this.meta_device_fields.includes(this.selectedValues[0].value)) {
      if (this.selectedValues.length > 1) this.selectedValues = this.selectedValues.slice(0, 1);
      return;
    }

    this.selectedValues.map((selected) => selected.value).forEach((value, index) => {
      index % 2 === 0
        ? this.search_by[value] = '' 
        : this.search_by[this.selectedValues[index - 1].value] = value;
    });

    const has_active_filter = Object.values(this.search_by).some((value) => value);

    this.filtered_managed_area_map = new Map();
    [...this.managed_area_map.entries()].forEach(([managed_area, devices]) => {
      let area_devices = devices;
      Object.entries(this.search_by)
        .filter(([field, value]) => value)
        .forEach(([field, value]) => {
          area_devices = area_devices.filter((device) => {
            const cabinet_field_value = field === 'cabinet_type' ? (device.class_name === 'virtual_cabinet' ? 'Virtual' : 'Regular') : device.meta_device[field];
            if (!cabinet_field_value) return false;
            return value.toString() === cabinet_field_value.toString();
          });
        });
      this.filtered_managed_area_map.set(managed_area, area_devices);
      if (area_devices.length) {
        const should_open = has_active_filter ? true : false;
        Vue.set(this.panel_is_open, managed_area, should_open);
        if (should_open) this.hydratePanel(managed_area);
      }else {
        this.filtered_managed_area_map.delete(managed_area);
        Vue.set(this.panel_is_open, managed_area, false);
      }
    });
  }

  filterByFreeSearch(){
    this.filtered_managed_area_map = new Map();
    [...this.managed_area_map.entries()].forEach(([managed_area, devices]) => {
      const matched_results = devices.filter((device) => (device.cabinet_id !== 'N/A' && device.cabinet_id === this.search) || device.description === this.search);
      this.filtered_managed_area_map.set(managed_area, matched_results);
      if (matched_results.length) {
        Vue.set(this.panel_is_open, managed_area, true);
        this.hydratePanel(managed_area);
      }else {
        Vue.set(this.panel_is_open, managed_area, false);
        this.filtered_managed_area_map.delete(managed_area);
      }
    });
  }

  removeSelectedField(index){
    if (this.selectedValues.length === 1 && !this.meta_device_fields.includes(this.selectedValues[0].value)){
      this.selectedValues = [];
      this.filtered_managed_area_map = this.managed_area_map;
    }else {
      delete this.search_by[this.selectedValues[index].value];
      this.selectedValues = this.selectedValues[index] && this.selectedValues[index + 1]
        ? this.selectedValues.slice(0, index).concat(this.selectedValues.slice(index + 2))
        : this.selectedValues.slice(0, index);
      this.selectedChanged();
    }
  }

  /**
   * Detail enrichment — applies a fetched detail row to a cabinet, derives
   * service-status fields, builds linked_inputs/outputs, seeds initial input/
   * output statuses from the streamed control/digital_input values, and
   * starts the MQTT listener.
   *
   * Extension-card cabinets follow a child-device branch (see enrichExtensionCabinet).
   */
  async enrichCabinet(cabinet, detail) {
    const detail_keys = [
      'sys___connected', 'dmodified_ts',
      'services.config', 'meta.devices_schedule', 'dashboard.connection_display',
      'device.connections', 'device.schedule', 'device.version.os_release',
      'control', 'digital_input'
    ];
    detail_keys.forEach((k) => {
      if (detail[k] !== undefined) Vue.set(cabinet, k, detail[k]);
    });

    cabinet.dashboard_view = cabinet['dashboard.connection_display'];
    cabinet.analytics_alerts_status = Utils.getAnalyticsAlertsServiceStatus(cabinet, this.projectTimezone);
    cabinet.devices_schedule_status = Utils.getCabinetDevicesSchedule(cabinet, this.projectTimezone);
    cabinet.power_validation_status = Utils.getPowerValidationServiceStatus(cabinet);
    cabinet.cabinet_alerts_status = Utils.getCabinetSatecAlertsServiceStatus(cabinet);
    cabinet.operation_window = Utils.getCabinetOperationWindow(cabinet, this.projectTimezone);

    if (cabinet.class_name === 'virtual_cabinet') {
      cabinet.linked_inputs = [];
      cabinet.linked_outputs = [];
      Vue.set(this.devices_status, cabinet.id, true);
      return;
    }

    const version = this.mqtt_version_by_class.has(cabinet.class_name)
      ? this.mqtt_version_by_class.get(cabinet.class_name).mqtt_version
      : 3;

    if (Utils.hasExtentionedTeltonicaClass(cabinet.class_name)) {
      await this.enrichExtensionCabinet(cabinet, version);
    } else {
      this.enrichRegularCabinet(cabinet, version);
    }
    Vue.set(this.devices_status, cabinet.id, true);
  }

  enrichRegularCabinet(cabinet, version) {
    const connections = cabinet['device.connections'] || {};
    cabinet.display_new_connections_screen = Utils.displayNewConnectionsScreen(cabinet['device.version.os_release'], connections);
    cabinet.linked_inputs = [];
    cabinet.linked_outputs = [];
    cabinet.schedule = cabinet['device.schedule'] || {};

    let input_num_field = 'gpio_address';
    let output_num_field = 'gpio_address';
    if (cabinet.display_new_connections_screen) {
      this.setLinkedInputsOutputsNewFormat(connections, cabinet);
    } else {
      this.setLinkedInputsOutputs(connections, cabinet);
      input_num_field = 'input_num';
      output_num_field = 'output_num';
    }
    cabinet.linked_inputs.sort((a, b) => a[input_num_field] - b[input_num_field]);
    cabinet.linked_outputs.sort((a, b) => a[output_num_field] - b[output_num_field]);

    this.seedStatusesFromStreams(cabinet, input_num_field, output_num_field);
    this.listener_ids_versions.set(cabinet.id, version);
    this.listenToMqtt(cabinet, version);
  }

  /**
   * Extension-card cabinets store schedule/connections/streams on a child device.
   * The bulk details fetch returned the parent's columns (which are empty for
   * these), so we do a focused per-cabinet child fetch here. This is the only
   * remaining per-cabinet properties/streams flow.
   */
  async enrichExtensionCabinet(cabinet, version) {
    cabinet.linked_inputs = [];
    cabinet.linked_outputs = [];
    cabinet.schedule = {};
    const child_id = await this.fetchCabinetChildId(cabinet.id);
    if (!child_id) return;

    cabinet.child = { id: child_id };
    cabinet.schedule = await this.fetchDeviceSchedule(child_id);
    const connections = await this.fetchDeviceConnections(child_id);
    this.setLinkedInputsOutputs(connections, cabinet);
    cabinet.linked_inputs.sort((a, b) => a.input_num - b.input_num);
    cabinet.linked_outputs.sort((a, b) => a.output_num - b.output_num);
    await this.fetchExtentionStreams(cabinet, child_id);
    this.listener_ids_versions.set(child_id, version);
    this.listenToChild(cabinet, child_id, version);
  }

  seedStatusesFromStreams(cabinet, input_num_field, output_num_field) {
    let digital_input: any = cabinet.digital_input || {};
    if (typeof digital_input === 'string') {
      try { digital_input = JSON.parse(Utils.convertToValidJson(digital_input)); } catch (e) { digital_input = {}; }
    }
    let control: any = cabinet.control || {};
    if (typeof control === 'string') {
      try { control = JSON.parse(Utils.convertToValidJson(control)); } catch (e) { control = {}; }
    }
    cabinet.linked_inputs.forEach((input) => {
      const status = digital_input[`${input[input_num_field]}`] || false;
      Vue.set(this.inputs_status[cabinet.id], input[input_num_field], status);
    });
    cabinet.linked_outputs.forEach((output) => {
      const status = control[`${output[output_num_field]}`] || false;
      Vue.set(this.outputs_status[cabinet.id], output[output_num_field], status);
    });
  }

  async fetchExtentionStreams(parent, child_id){
    await Promise.all(parent.linked_inputs.map(async (input) => {
      let status = await this.fetchStreamLatestValue(child_id, `input_${input.input_num}`) || 0;
      status = typeof status === 'string' ? Number(status) : status;
      Vue.set(this.inputs_status[parent.id], input.input_num, status ? true : false);
    }));

    await Promise.all(parent.linked_outputs.map(async (output) => {
      let status = await this.fetchStreamLatestValue(child_id, `relay_${output.output_num}`) || 0;
      status = typeof status === 'string' ? Number(status) : status;
      Vue.set(this.outputs_status[parent.id], output.output_num, status ? true : false);
    }));
  }

  checkInputIsOpposite(config){
    return config.toLowerCase().includes('1=tripped') || config.toLowerCase().includes('1=off');
  }

  checkOutputIsOpposite(config){
    return config.toLowerCase().includes('1=off');
  }

  getInputColor(input_config, status){
    const config_is_opposite = this.checkInputIsOpposite(input_config);
    const input_is_on = config_is_opposite ? !status : status;
    return input_is_on ? '#7FB77E' : '#E97777';
  }

  getInputColorNewFormat(status){
    return status ? '#7FB77E' : '#E97777';
  }

  getOutputStatus(output_config, status){
    if (typeof status !== 'boolean') return status;
    const config_is_opposite = this.checkOutputIsOpposite(output_config);
    return config_is_opposite ? !status : status;
  }

  getOutputStatusNewFormat(output_config, status){
    if (typeof status !== 'boolean') return status;
    const config_is_opposite = false;
    return config_is_opposite ? !status : status;
  }

  async fetchCabinetChildId(device_id){
    try {
      const childrens_id = await API.get(Types.PROJECTS, this.project.id + '/devices/' + device_id + '/children/');
      return childrens_id && childrens_id.length && childrens_id[0] || '';
    } catch (e) {
      return '';
    }
  }

  setLinkedInputsOutputsNewFormat(connections, device){
    if (Array.isArray(connections)) {
      device.linked_inputs = connections.filter((connection) => connection.type === 'input');
      device.linked_outputs = connections.filter((connection) => connection.type === 'output')
        .map((output) => ({...output, ...Utils.getTodayScheduleTime(device.schedule, output.gpio_address)}))
        .filter((output) => output.on_time);
    }else {
      device.linked_inputs = [];
      device.linked_outputs = [];
    }
  }

  setLinkedInputsOutputs(connections, device){
    Object.values(connections).forEach((connection) => {
      const elements = connection['elements'] && Object.values(connection['elements']);
      this.getChildInputOutput(elements, device, connection['desc']);
    });
  }

  getChildInputOutput(elements, device, connection_description){
    elements.forEach((element) => {
      if (element['control']) {
        device.linked_outputs.push({ output_num: element['control'], configuration: element.configuration, on_time: this.getTime('on', device, element['control']) , off_time: this.getTime('off', device, element['control']) });
      }else {
        device.linked_inputs.push({ input_num: element['input'], description: connection_description, configuration: element.configuration });
      }
      if (!element.hasOwnProperty('children')) return;
      if (element.hasOwnProperty('children') && element['children'].length) this.getChildInputOutput(element['children'], device, connection_description);
    });
  }

  getLinkedManagedArea(managed_area){
    let ma = managed_area.toString();
    if (ma.toLowerCase().includes('road') || ma.toLowerCase().includes('כביש') || ma.includes('/') || ma.match(/\\/)){
      let final_ma = '';
      ma = ma.toLowerCase();
      if (ma.includes('road')){
        final_ma = 'Road ';
        ma = ma.replace('road', '');
      }else if (ma.includes('כביש')){
        final_ma = 'כביש ';
        ma = ma.replace('כביש', ''); 
      }
      ma = ma.trim();
      if (this.isNumeric(ma[0])){
        if (ma.match(/\\/)){
          ma = ma.split('\\')[0];
        }else if (ma.includes('/')){
          ma = ma.split('/')[0];
        }
        let index = 1;
        let numeric = true;
        while (index < 4 && numeric && index < ma.length){
          numeric = this.isNumeric(ma[index]);
          if (numeric) index++;
        }
        return final_ma + ma.slice(0, index);
      }else return 'Other';
    }
    return ma.trim();
  }

  isNumeric(character){
    return /\d/.test(character);
  }

  async fetchDeviceConnections(device_id){
    let device_connections = await this.fetchSingleProperty(device_id, 'device.connections');
    device_connections = device_connections && device_connections.value || {};
    return device_connections;
  }

  async fetchDeviceSchedule(device_id){
    let device_schedule = await this.fetchSingleProperty(device_id, 'device.schedule');
    device_schedule = device_schedule && device_schedule.value || {};
    return device_schedule;
  }

  async fetchSingleProperty(device_id, property_name) {
    try {
      const response = await API.get(Types.DEVICES, `${device_id}/properties`, { name: property_name });
      return (response && response.results && response.results[0]) || null;
    } catch (e) {
      return null;
    }
  }

  async fetchStreamLatestValue(device_id, stream_name) {
    try {
      const response = await API.get(Types.DEVICES, `${device_id}/streams/${device_id}.${stream_name}/latest`);
      return (response && response.lastvalue) || null;
    } catch (e) {
      return null;
    }
  }

  moveToCabinetDetails(device, selected_managed_area) {
    this.setSelectedManagedArea(selected_managed_area);
    this.$router.push(`/cabinet-details/${device.id}`);
  }

  onCabinetClick({ cabinet, managed_area }) {
    this.moveToCabinetDetails(cabinet, managed_area);
  }

  getCabinetStatusIcon(device) {
    let src = require('@/assets/images/mapView_icons/cabinet/cabinet_fault.svg');
    try {
      if (device.sys___active === 0) src = require('@/assets/images/mapView_icons/cabinet/cabinet_fault.svg');
      else if (device.sys___connected === 1) src = require('@/assets/images/cabinet_details_icons/cabinet_connected.svg');
      else if (device.sys___connected === 0) src = require('@/assets/images/cabinet_details_icons/cabinet_disconnected.svg');
    } catch (err) {}
    return src;
  }

  listenToMqtt(device, version) {
    if (!this.mqtt || !device) return;

    const topic = this.mqtt.getSingleDeviceSubscribe(device.id, version);

    this.mqtt.listen(topic, async (msg, match) => {
        const mqtt_msg_data = this.mqtt.getMqttMsgData(msg, match, version);
        if (mqtt_msg_data.message_type !== 'evt' || !mqtt_msg_data.stream_name) return;

        // Legacy consolidated JSON streams.
        if (mqtt_msg_data.format === 'json' &&
          (mqtt_msg_data.stream_name === 'control' || mqtt_msg_data.stream_name === 'digital_input')
        ){
          if (mqtt_msg_data.stream_name === 'control') this.updateOutputs(mqtt_msg_data.message, device);
          else this.updateInputs(mqtt_msg_data.message, device);
          return;
        }

        // New format: individual input_N / relay_N streams. State is keyed by
        // gpio_address, so we map num -> gpio_address via linked_inputs/outputs.
        if (device.display_new_connections_screen) {
          if (mqtt_msg_data.stream_name.startsWith('input_')) {
            const input_num = this.getInputOutputNumber(mqtt_msg_data.stream_name);
            if (!input_num) return;
            const linked = (device.linked_inputs || []).find((i) => Number(i.input_num) === Number(input_num));
            if (!linked) return;
            const value = Number(mqtt_msg_data.message) ? true : false;
            Vue.set(this.inputs_status[device.id], linked.gpio_address, value);
          } else if (mqtt_msg_data.stream_name.startsWith('relay_')) {
            const output_num = this.getInputOutputNumber(mqtt_msg_data.stream_name);
            if (!output_num) return;
            const linked = (device.linked_outputs || []).find((o) => Number(o.output_num) === Number(output_num));
            if (!linked) return;
            const value = Number(mqtt_msg_data.message) ? true : false;
            Vue.set(this.outputs_status[device.id], linked.gpio_address, value);
          }
        }
      }
    );

    this.publishMqttMessage('-1', 'control', device);
    this.publishMqttMessage('-1', 'digital_input', device);
  }

  listenToChild(parent, child_id, version) {
    if (!this.mqtt || !child_id) return;

    this.mqtt.listen(
      this.mqtt.getSingleDeviceSubscribe(child_id, version),
      async (msg, match) => {
        const mqtt_msg_data = this.mqtt.getMqttMsgData(msg, match, version);

        if (mqtt_msg_data.message_type === 'evt' &&
          mqtt_msg_data.stream_name && 
          (mqtt_msg_data.stream_name.includes('input_') || mqtt_msg_data.stream_name.includes('relay_'))
        ){
          if (mqtt_msg_data.stream_name.includes('input_')){
            const input_num = this.getInputOutputNumber(mqtt_msg_data.stream_name);
            if (input_num && input_num >= 1 && input_num <= 12){
              Vue.set(this.inputs_status[parent.id], input_num, Number(mqtt_msg_data.message) ? true : false);
            }
          }else {
            const output_num = this.getInputOutputNumber(mqtt_msg_data.stream_name);
            if (output_num && output_num >= 1 && output_num <= 4){
              Vue.set(this.outputs_status[parent.id], output_num, Number(mqtt_msg_data.message) ? true : false);
            }
          }
        }
      }
    );
  }

  getInputOutputNumber(stream_name){
    const [start, num] = stream_name.split('_');
    return !isNaN(num) && Number(num) || '';
  }

  publishMqttMessage(message, streamName, device) {
    const mqtt_publish_data = {
      version_num: 3,
      device_id: device.id,
      stream_id: '',
      message_type: 'cmd',
      format: 'minimal',
      stream_value: message,
      options: {
        retain: false,
        qos: 1
      }
    };

    if (this.mqtt_version_by_class.has(device.class_name)) {
      mqtt_publish_data.version_num = this.mqtt_version_by_class.get(device.class_name).mqtt_version;
    }

    mqtt_publish_data.stream_id = mqtt_publish_data.version_num === 3 ? `${device.id}.${streamName}` : streamName;
    this.mqtt.publishToSingleDevice(mqtt_publish_data);
  }

  getValidJson(message) {
    let parsed_message;
    try{
      parsed_message = JSON.parse(message);
    }catch (e){
      parsed_message = { content: ''};
    }
    return parsed_message;
  }

  mqttGetMsg(format, message) {
    return format === 'json' ? message : format === 'minimal' ? Number(message) : Number(message['content']);
  }

  clearAllClicked(){
    this.search_by = {};
    this.filtered_managed_area_map = this.managed_area_map;
  }

  updateOutputs(msg, device) {
    const control_stream = JSON.parse(JSON.stringify(msg));
    const linked = device.linked_outputs || [];
    const use_gpio = !!device.display_new_connections_screen;
    Object.entries(control_stream).forEach(([output, value]) => {
      const output_num = Number(output);
      let key: any = output_num;
      if (use_gpio) {
        const match = linked.find((o) => Number(o.output_num) === output_num);
        if (!match) return;
        key = match.gpio_address;
      }
      if (this.outputs_status[device.id].hasOwnProperty(key)) {
        Vue.set(this.outputs_status[device.id], key, value);
      }
    });
  }

  updateInputs(msg, device) {
    const digital_input_stream = JSON.parse(JSON.stringify(msg));
    const linked = device.linked_inputs || [];
    const use_gpio = !!device.display_new_connections_screen;
    Object.entries(digital_input_stream).forEach(([input, value]) => {
      const input_num = Number(input);
      let key: any = input_num;
      if (use_gpio) {
        const match = linked.find((i) => Number(i.input_num) === input_num);
        if (!match) return;
        key = match.gpio_address;
      }
      if (this.inputs_status[device.id].hasOwnProperty(key)) {
        Vue.set(this.inputs_status[device.id], key, value);
      }
    });
  }

  getTitleOperation(device, output) {
    const schedule = device.schedule[`${output}`] || null;
    if (schedule && schedule['is_astronomical'] === true) return 'Astral';
    if (schedule && schedule['is_astronomical'] === false) return 'Clock';
    return 'N/A';
  }

  getTime(onOff, device, output) {
    if (this.next_activation_by_cabinet.has(device.id)) {
      const time = this.getNextActivationTime(onOff, device.id, output);
      if (time !== null) return time;
    }

    const schedule = device.schedule[`${output}`] || null;
    let time = '';
    if (schedule && schedule['is_astronomical'] === true){
      if (onOff === 'on'){
        time = schedule.dusk;
        time = Utils.getAstronomicalTime(time, this.dusk_dawn_data.sunset_start_timestamp, this.dusk_dawn_data.sunset_start, this.projectTimezone);
      }else{
        time = schedule.dawn;
        time = Utils.getAstronomicalTime(time, this.dusk_dawn_data.sunrise_timestamp, this.dusk_dawn_data.sunrise, this.projectTimezone);
      }
    }else {
      time = schedule && schedule[onOff] || '';
    }
    return time;
  }

  getNextActivationTime(onOff, device_id, output){
    const next_activation_times = this.next_activation_by_cabinet.get(device_id)
        .filter(([timestamp, value]) => value && value.output === output);

    if (next_activation_times.length) {
      const time = next_activation_times[next_activation_times.length - 1][1][onOff === 'on' ? 'on_time' : 'off_time'];
      return time.split(' ')[1].slice(0, 5);
    }else return null;
  }

  beforeDestroy() {
    [...this.listener_ids_versions.entries()].forEach(([device_id, version]) => {
      const device_topic = this.mqtt.getSingleDeviceSubscribe(device_id, version);
      this.mqtt.unlisten(device_topic);
    });
    this.mqtt.clearListeners();
    const input = this.$refs.search_combobox['$el'].querySelector('input');
    if (input) {
      input.removeEventListener('keydown', this.onKeydown);
    }
  }
}

export default CabinetManagement;
